import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { WidgetSkeletonComponent } from '../widget-skeleton/widget-skeleton.component';
import { WidgetSkeletonBlock } from '../widget-skeleton/widget-skeleton.model';

/**
 * The shell every dashboard widget renders inside: a `.card` with a title, an optional subtitle, a
 * right-hand action slot, and the three states a widget that fetches its own data always has —
 * loading, error and empty. It exists so a widget owns its states without each one re-implementing
 * the same skeleton, the same "nothing to show" sentence, and the same retry affordance.
 *
 * Content is projected: `[cardAction]` lands in the header's right slot, everything else is the body
 * (rendered only once the widget is loaded, has no error, and is not empty). The loading placeholder is
 * NOT projected — it is declared as `config.skeleton` so the shell that shows it while a widget's chunk
 * downloads and the widget that shows it while its data loads render the identical shape.
 */
export interface WidgetCardConfig {
  title: string;
  subtitle?: string;
  icon?: string;
  /**
   * The loading placeholder, in the shape of this card's real body. Declared rather than drawn so both
   * loading phases can render it; `skeletonRows` generic bars are the fallback when it is absent.
   */
  skeleton?: WidgetSkeletonBlock[];
  skeletonRows?: number;
  /** The sentence shown when the widget loaded successfully with nothing to show. */
  emptyText?: string;
  /** Icon for that sentence; falls back to the card's own. */
  emptyIcon?: string;
  /**
   * Where the reader can go instead. An empty card that only states the absence leaves them stuck —
   * "no leave booked" is worth far more next to a way to book some.
   */
  emptyAction?: { label: string; link: string | (string | number)[] };
}

@Component({
  selector: 'app-widget-card',
  standalone: true,
  imports: [RouterLink, IconComponent, SkeletonComponent, WidgetSkeletonComponent],
  templateUrl: './widget-card.component.html',
  styleUrl: './widget-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCardComponent {
  readonly config = input.required<WidgetCardConfig>();
  readonly loading = input(false);
  /** Message from `ApiErrorHelper.firstMessage` — shown instead of the body, never as a toast. */
  readonly error = input<string | null>(null);
  readonly empty = input(false);

  readonly retry = output<void>();

  get skeletonRows(): number[] {
    return Array.from({ length: this.config().skeletonRows ?? 3 }, (_, i) => i);
  }
}
